import { observer } from 'mobx-react-lite';
import { useWatch } from 'react-hook-form';
import type { EventPanelFormType, SensorAlertBundle } from '@mark43/cad-api';
import { Box, Button, Flex, Text } from '@cad-arc';
import { useEventMetadataStore } from '../event-panel/event-panel-form/event-metadata';
import { EventPanelTab } from '../event-panel/tabs/components/event-panel-tab';
import { getEventLinkedSensorAlerts } from './helpers';
import { SensorEventCard } from './sensor-event-card';
import { useOpenSensorPanelsOnMap } from './sensor-panel-map-actions';
import { useSensorsStore } from './use-sensors-store';

/**
 * Links out to the two panels, in the map window — where sensor panels live. This tab is in the
 * events window, so opening them here would put a camera panel over the event panels.
 */
function SensorEventPanelLinks({ alert }: { alert: SensorAlertBundle }): React.ReactElement {
    const { openSensorEventPanelOnMap, openSensorPanelOnMap } = useOpenSensorPanelsOnMap();

    return (
        <Flex justifyContent="flex-end" gap={2}>
            <Button
                size="sm"
                trailingVisual="OpenNewWindow"
                onClick={() => openSensorEventPanelOnMap(alert)}
            >
                Sensor event
            </Button>
            <Button
                size="sm"
                trailingVisual="OpenNewWindow"
                onClick={() => openSensorPanelOnMap(alert.sensorExternalId)}
            >
                Sensor
            </Button>
        </Flex>
    );
}

/**
 * The sensor events linked to this CAD event (design 7a): the drawer's card, for review only — no
 * event actions, no archive, no unlink. Links out to the sensor event and the camera.
 *
 * **Live without a subscription of its own.** The list is the event panel's metadata, which the
 * server re-pushes whenever one of the event's links changes — from the drawer, the map, another
 * dispatcher or the event's own save — plus the window's sensor store, which the stream updates
 * when it republishes a relinked alert. See `getEventLinkedSensorAlerts`. The events window never
 * toasts: `SensorAlertListener` leaves toasting to the map window.
 *
 * **A new event shows what it will be linked to.** Create event on a sensor event opens this panel
 * with the alert already on the form's `sensorAlertKeys`, and the save links it; until then the
 * card is shown here so the dispatcher can see it is coming along.
 */
export const SensorEventsTab = observer(function SensorEventsTab(): React.ReactElement {
    const eventMetadata = useEventMetadataStore().state.metadata;
    const eventId = eventMetadata?.eventId;
    const sensorsStore = useSensorsStore();
    const pendingAlertKeys = useWatch<EventPanelFormType, 'sensorAlertKeys'>({
        name: 'sensorAlertKeys',
    });

    const isNewEvent = eventId === undefined;
    const alerts = isNewEvent
        ? sensorsStore.alerts.filter((alert) =>
              (pendingAlertKeys ?? []).includes(alert.alertKey)
          )
        : getEventLinkedSensorAlerts(eventId, eventMetadata?.sensorAlerts ?? [], sensorsStore.alerts);

    return (
        <EventPanelTab title={`Sensor Events (${alerts.length})`}>
            {alerts.length === 0 ? (
                <Text variant="caption" color="secondary">
                    {isNewEvent
                        ? 'Save this event to attach sensor events to it.'
                        : 'No sensor events are attached to this event.'}
                </Text>
            ) : (
                <>
                    {isNewEvent ? (
                        <Text variant="caption" color="secondary">
                            Attached when this event is saved.
                        </Text>
                    ) : undefined}
                    <Box as="ul" margin={0} padding={0} data-testid="EVENT_PANEL_SENSOR_EVENTS">
                        {alerts.map((alert) => (
                            <SensorEventCard
                                key={alert.alertKey}
                                alert={alert}
                                timestamp="dateTime"
                            >
                                <SensorEventPanelLinks alert={alert} />
                            </SensorEventCard>
                        ))}
                    </Box>
                </>
            )}
        </EventPanelTab>
    );
});
